import React from 'react';
import { 
  HeartIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface HealthTest {
  id: string;
  testName: string;
  result: string;
  testDate: string | Date;
}

interface BirdHealthIndicatorProps {
  healthTests: HealthTest[];
  size?: 'sm' | 'md' | 'lg';
  showCount?: boolean;
}

export default function BirdHealthIndicator({ 
  healthTests, 
  size = 'md', 
  showCount = true 
}: BirdHealthIndicatorProps) {
  const getHealthStatus = () => {
    if (healthTests.length === 0) {
      return { status: 'none', color: 'text-slate-400', bgColor: 'bg-gray-100', icon: HeartIcon };
    }

    // Get recent tests (last 12 months)
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
    
    const recentTests = healthTests.filter(test => 
      new Date(test.testDate) >= twelveMonthsAgo
    );

    if (recentTests.length === 0) {
      return { status: 'outdated', color: 'text-yellow-600', bgColor: 'bg-yellow-100', icon: ClockIcon };
    }

    // Check for any concerning results
    const concerningResults = recentTests.filter(test => {
      const result = test.result.toLowerCase();
      return result.includes('positive') || result.includes('abnormal') || result.includes('critical');
    });

    if (concerningResults.length > 0) {
      return { status: 'concern', color: 'text-red-600', bgColor: 'bg-red-100', icon: ExclamationTriangleIcon };
    }

    // Check for pending results
    const pendingResults = recentTests.filter(test => {
      const result = test.result.toLowerCase();
      return result.includes('pending') || result.includes('inconclusive');
    });

    if (pendingResults.length > 0) {
      return { status: 'pending', color: 'text-yellow-600', bgColor: 'bg-yellow-100', icon: ClockIcon };
    }

    // All good
    return { status: 'good', color: 'text-green-600', bgColor: 'bg-green-100', icon: CheckCircleIcon };
  };

  const { status, color, bgColor, icon: Icon } = getHealthStatus();

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  const containerSizeClasses = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2'
  };

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  const getStatusText = () => {
    switch (status) {
      case 'none':
        return 'No health tests';
      case 'outdated':
        return 'Tests outdated';
      case 'concern':
        return 'Health concerns';
      case 'pending':
        return 'Tests pending';
      case 'good':
        return 'Health good';
      default:
        return 'Unknown';
    }
  };

  return (
    <div 
      className={`inline-flex items-center space-x-1 rounded-full ${bgColor} ${containerSizeClasses[size]}`}
      title={getStatusText()}
    >
      <Icon className={`${sizeClasses[size]} ${color}`} />
      {showCount && (
        <span className={`font-medium ${color} ${textSizeClasses[size]}`}>
          {healthTests.length}
        </span>
      )}
    </div>
  );
} 